import { makeStyles } from "@material-ui/core/styles";

/** Styles for the Provision card and its sections — kept independent of other cards' styles
 * so this stays a genuinely self-contained unit (mirrors ProfessionalDetails.styles.js). */
const useStyles = makeStyles((theme) => ({
  card: {
    backgroundColor: "var(--color-background-paper)",
    border: "1px solid var(--total_row_boldbackground)",
    borderRadius: "calc(12px * var(--app-radius-scale, 1))",
  },
  cardHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(1.5, 2, 0),
    flexWrap: "wrap",
    gap: theme.spacing(1),
  },
  cardHeaderTitle: {
    fontSize: "0.9375rem",
    fontWeight: 700,
    color: "var(--color-text-primary)",
  },
  cardBody: {
    padding: theme.spacing(1.75, 2, 2),
  },
  title: {
    fontSize: "1.05rem",
    fontWeight: 700,
    color: "var(--color-text-primary)",
    margin: 0,
  },
  subtle: {
    color: "var(--color-text-secondary)",
    fontSize: "0.75rem",
  },
  hint: {
    color: "var(--color-text-secondary)",
    fontSize: "0.6875rem",
  },
  sectionHeading: {
    fontSize: "0.6875rem",
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    color: "var(--color-text-secondary)",
    fontWeight: 700,
    margin: theme.spacing(1.5, 0, 0.75),
  },
  headerBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  actions: {
    display: "flex",
    gap: theme.spacing(1),
  },
  editNote: {
    display: "flex",
    gap: theme.spacing(1),
    alignItems: "flex-start",
    backgroundColor: "color-mix(in srgb, var(--color-warning-main, #b78103) 10%, transparent)",
    border: "1px solid var(--color-warning-main, #b78103)",
    color: "var(--color-warning-main, #b78103)",
    borderRadius: "calc(8px * var(--app-radius-scale, 1))",
    padding: theme.spacing(1, 1.5),
    fontSize: "0.8125rem",
    marginBottom: theme.spacing(2),
  },
  grid2: {
    display: "grid",
    gridTemplateColumns: "1.35fr 1fr",
    gap: theme.spacing(2.5),
    alignItems: "start",
    [theme.breakpoints.down("md")]: { gridTemplateColumns: "1fr" },
  },
  grid3: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: theme.spacing(2.5),
    alignItems: "start",
    [theme.breakpoints.down("sm")]: { gridTemplateColumns: "1fr" },
  },
  frow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" },
  },
  /**
   * MUI v4 paints `.MuiTableCell-head` with the static light theme's near-black text
   * (defaultTheme's dark-mode override lands on `.MuiTableCell-root`, which the later `head`
   * rule outranks at equal specificity) — so headings all but vanished on a dark surface. These
   * descendant selectors are specific enough to win in both themes, and they pull the same
   * uppercase-secondary heading treatment the Documents Dashboard tables already use.
   */
  table: {
    "& .MuiTableHead-root .MuiTableCell-root": {
      color: "var(--color-text-secondary)",
      fontSize: "0.6875rem",
      fontWeight: 700,
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      borderBottomColor: "var(--total_row_boldbackground)",
      whiteSpace: "nowrap",
    },
    "& .MuiTableBody-root .MuiTableCell-root": {
      color: "var(--color-text-primary)",
      borderBottomColor: "color-mix(in srgb, var(--total_row_boldbackground) 70%, transparent)",
    },
  },
  /**
   * Provision / Weighting cells in the Scenarios table. MUI's `size="small"` TableCell spends
   * 40px of a narrow column on its own left/right padding, which (on top of the outlined input's
   * padding and the "%" adornment) left barely a character's worth of room — "10" and "100" were
   * clipped mid-value. Trim the cell padding here rather than widening the column further, so
   * Description keeps the space it needs.
   */
  numericCell: {
    paddingLeft: 8,
    paddingRight: 8,
  },
  /**
   * The 0.00-100.00% inputs those cells hold. Reclaims the rest of the room from the outlined
   * input's own right padding and the adornment's left margin (the "%" glyph carries an inline
   * marginRight of its own from A_TextField, which is all the separation it needs), and
   * right-aligns the digits so they sit against the "%" the way a figure in a table should.
   */
  percentField: {
    minWidth: 96,
    "& [class*='MuiOutlinedInput-adornedEnd']": {
      paddingRight: 0,
    },
    "& [class*='MuiInputAdornment-positionEnd']": {
      marginLeft: 0,
    },
    "& [class*='MuiInputBase-input']": {
      paddingLeft: 8,
      paddingRight: 0,
      textAlign: "right",
    },
  },
  calcBadge: {
    display: "inline-flex",
    alignItems: "center",
    backgroundColor: "color-mix(in srgb, var(--color-primary-main) 12%, transparent)",
    color: "var(--color-primary-text)",
    fontSize: "0.625rem",
    fontWeight: 700,
    padding: "1.5px 7px",
    borderRadius: "calc(5px * var(--app-radius-scale, 1))",
    marginLeft: theme.spacing(0.75),
    textTransform: "uppercase",
    letterSpacing: "0.03em",
  },
  dropzone: {
    border: "2px dashed var(--total_row_boldbackground)",
    borderRadius: "calc(10px * var(--app-radius-scale, 1))",
    padding: theme.spacing(4, 2.5),
    textAlign: "center",
    color: "var(--color-text-secondary)",
    backgroundColor: "var(--color-background-default)",
    transition: "border-color 0.2s ease, background-color 0.2s ease",
  },
  dropzoneHover: {
    borderColor: "var(--color-primary-main)",
    backgroundColor: "color-mix(in srgb, var(--color-primary-main) 6%, transparent)",
  },
  filehint: {
    color: "var(--color-text-secondary)",
    fontSize: "0.7rem",
    marginTop: theme.spacing(1.5),
    display: "flex",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: theme.spacing(0.5),
  },
  conditional: {
    border: "1px dashed var(--total_row_boldbackground)",
    borderRadius: "calc(9px * var(--app-radius-scale, 1))",
    padding: theme.spacing(2, 2, 0.5),
    marginTop: theme.spacing(0.5),
    backgroundColor: "var(--color-background-default)",
  },
  outcome: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(2),
    paddingTop: theme.spacing(1.75),
    borderTop: "1px solid var(--total_row_boldbackground)",
  },
  outcomeLabel: {
    color: "var(--color-text-primary)",
    fontWeight: 600,
    fontSize: "0.8125rem",
  },
  outcomeValue: {
    fontSize: "1.125rem",
    fontWeight: 800,
    color: "var(--color-primary-text)",
  },
  linkAction: {
    color: "var(--color-primary-text)",
    cursor: "pointer",
    fontSize: "0.8125rem",
    fontWeight: 600,
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    background: "none",
    border: "none",
    "&:hover": { textDecoration: "underline" },
    "&:disabled": { color: "var(--color-text-secondary)", cursor: "default", textDecoration: "none" },
  },
  validationSummary: {
    color: "var(--color-error-main, #d32f2f)",
    fontSize: "0.8125rem",
    backgroundColor: "color-mix(in srgb, var(--color-error-main, #d32f2f) 10%, transparent)",
    border: "1px solid var(--color-error-main, #d32f2f)",
    borderRadius: "calc(8px * var(--app-radius-scale, 1))",
    padding: theme.spacing(1, 1.5),
    marginBottom: theme.spacing(1.5),
  },
  /*
   * ProfessionalHoverPopover (OQ2 — hover a professional's name to preview their email and
   * contact number). The panel itself is a MUI Tooltip, restyled from the default dark chip into
   * a small card on the app's own paper surface, because it carries three lines of real content
   * rather than a one-line label.
   */
  previewAnchor: {
    // Wraps a whole form field, so it must not change the field's layout: block, full width, and
    // no padding/margin of its own.
    display: "block",
    width: "100%",
  },
  previewName: {
    fontWeight: 600,
    color: "var(--color-primary-text)",
    borderBottom: "1px dotted currentColor",
    cursor: "help",
    outlineOffset: 2,
  },
  previewTooltip: {
    background: "var(--color-background-paper)",
    border: "1px solid var(--total_row_boldbackground)",
    boxShadow: "0 8px 24px rgba(16,42,59,.16)",
    borderRadius: "calc(9px * var(--app-radius-scale, 1))",
    padding: theme.spacing(1.25, 1.5),
    maxWidth: 320,
    color: "var(--color-text-primary)",
    fontSize: "0.75rem",
  },
  previewArrow: {
    color: "var(--color-background-paper)",
  },
  previewBody: {
    minWidth: 180,
  },
  previewHeading: {
    fontWeight: 700,
    marginBottom: theme.spacing(0.5),
    color: "var(--color-text-primary)",
  },
  previewCompany: {
    fontWeight: 400,
    color: "var(--color-text-secondary)",
    "&::before": {
      content: '" · "',
    },
  },
  previewRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
    color: "var(--color-text-secondary)",
    marginTop: theme.spacing(0.375),
    wordBreak: "break-word",
  },
}));

export default useStyles;
